/**
 * Status system for XSTRONG POWER EV Charger Service Management
 * Primary: new 9-status service pipeline
 * Legacy: 9-step pickup pipeline (as per XSTRONG POWER spec)
 */
import {
  ClipboardList, Search, Clock, Wrench, CheckCircle2,
  RefreshCw, Package, Star, XCircle,
  PhoneCall, CalendarCheck, QrCode, PackageCheck,
  Truck, PackageOpen, Send, Navigation
} from "lucide-react";

// ── Primary service management statuses ──────────────────────────────────────
export const PIPELINE = [
  { status:"Received",               icon:ClipboardList, dot:"#64748b", bg:"bg-slate-100",  text:"text-slate-700",   border:"border-slate-200",  dotCls:"bg-slate-400",   desc:"Service entry received",                 emoji:"📋" },
  { status:"Under Inspection",        icon:Search,       dot:"#2563eb", bg:"bg-blue-50",    text:"text-blue-800",    border:"border-blue-200",   dotCls:"bg-blue-500",    desc:"Device being inspected",                 emoji:"🔍" },
  { status:"Waiting for Approval",    icon:Clock,        dot:"#d97706", bg:"bg-amber-50",   text:"text-amber-800",   border:"border-amber-200",  dotCls:"bg-amber-500",   desc:"Awaiting customer / admin approval",     emoji:"⏳" },
  { status:"Repair in Progress",      icon:Wrench,       dot:"#ea580c", bg:"bg-orange-50",  text:"text-orange-800",  border:"border-orange-200", dotCls:"bg-orange-500",  desc:"Repair underway by technician",          emoji:"🔧" },
  { status:"Repaired",                icon:CheckCircle2, dot:"#059669", bg:"bg-emerald-50", text:"text-emerald-800", border:"border-emerald-200",dotCls:"bg-emerald-500", desc:"Repair complete",                         emoji:"✅" },
  { status:"Replacement Approved",    icon:RefreshCw,    dot:"#7c3aed", bg:"bg-purple-50",  text:"text-purple-800",  border:"border-purple-200", dotCls:"bg-purple-500",  desc:"Replacement unit approved",              emoji:"🔄" },
  { status:"Replacement Dispatched",  icon:Package,      dot:"#4f46e5", bg:"bg-indigo-50",  text:"text-indigo-800",  border:"border-indigo-200", dotCls:"bg-indigo-500",  desc:"Replacement dispatched to customer",     emoji:"📦" },
  { status:"Completed",               icon:Star,         dot:"#16a34a", bg:"bg-green-50",   text:"text-green-800",   border:"border-green-200",  dotCls:"bg-green-600",   desc:"Service successfully completed",         emoji:"🎉" },
  { status:"Warranty Rejected",       icon:XCircle,      dot:"#e11d48", bg:"bg-rose-50",    text:"text-rose-800",    border:"border-rose-200",   dotCls:"bg-rose-500",    desc:"Warranty claim not accepted",            emoji:"❌" },
];

// ── 9-Step Pipeline (as per XSTRONG POWER spec) ──────────────────────────────
// NOTE: "In Transit" appears at step 5 (to service center) AND step 9 (return to customer).
// Use getLegacyPipelineIdx(status, history) to resolve the correct position.
export const LEGACY_PIPELINE = [
  { status:"Pick Up Request",   icon:PhoneCall,    dot:"#7c3aed", bg:"bg-violet-50",  text:"text-violet-800",  border:"border-violet-200", dotCls:"bg-violet-500", desc:"Customer has requested a pickup",         emoji:"📞" },
  { status:"Pick Up Accepted",  icon:CalendarCheck,dot:"#4f46e5", bg:"bg-indigo-50",  text:"text-indigo-800",  border:"border-indigo-200", dotCls:"bg-indigo-500", desc:"Pickup confirmed and scheduled",          emoji:"📅" },
  { status:"Barcode Generated", icon:QrCode,       dot:"#0891b2", bg:"bg-cyan-50",    text:"text-cyan-800",    border:"border-cyan-200",   dotCls:"bg-cyan-500",   desc:"Tracking barcode created for device",    emoji:"🏷️" },
  { status:"Picked Up",         icon:PackageCheck, dot:"#0284c7", bg:"bg-sky-50",     text:"text-sky-800",     border:"border-sky-200",    dotCls:"bg-sky-500",    desc:"Device collected from customer",          emoji:"📦" },
  { status:"In Transit",        icon:Truck,        dot:"#1d4ed8", bg:"bg-blue-50",    text:"text-blue-800",    border:"border-blue-200",   dotCls:"bg-blue-500",   desc:"Device on the way to service center",     emoji:"🚚" },
  { status:"Received",          icon:PackageOpen,  dot:"#475569", bg:"bg-slate-100",  text:"text-slate-700",   border:"border-slate-200",  dotCls:"bg-slate-400",  desc:"Device arrived at service center",        emoji:"🏪" },
  { status:"Repairing",         icon:Wrench,       dot:"#d97706", bg:"bg-amber-50",   text:"text-amber-800",   border:"border-amber-200",  dotCls:"bg-amber-500",  desc:"Device under repair",                     emoji:"🔧" },
  { status:"In Dispatch",       icon:Send,         dot:"#ea580c", bg:"bg-orange-50",  text:"text-orange-800",  border:"border-orange-200", dotCls:"bg-orange-500", desc:"Repair done, packed for dispatch",        emoji:"📮" },
  { status:"In Transit",        icon:Navigation,   dot:"#16a34a", bg:"bg-green-50",   text:"text-green-800",   border:"border-green-200",  dotCls:"bg-green-600",  desc:"Device on the way back to customer",      emoji:"🚚" },
];

// ── Unique status list for dropdowns (deduplicated) ───────────────────────────
// "In Transit" appears twice in LEGACY_PIPELINE — only list it once in dropdowns
const _legacyUnique = Array.from(new Set(LEGACY_PIPELINE.map(s => s.status)));

// All valid statuses combined (deduped)
export const ALL_STATUSES = [
  ...PIPELINE.map(s => s.status),
  ..._legacyUnique,
];

// ── Context-aware pipeline index ──────────────────────────────────────────────
// Required because "In Transit" appears at both index 4 (to center) and index 8 (to customer).
// Uses status_history to determine which occurrence is correct.
export const getLegacyPipelineIdx = (status, statusHistory = []) => {
  if (!status) return 0;
  const indices = LEGACY_PIPELINE.reduce((acc, s, i) => {
    if (s.status === status) acc.push(i);
    return acc;
  }, []);
  if (indices.length === 0) return -1;
  if (indices.length === 1) return indices[0];
  // Duplicate status name — determine context from history:
  // If history contains "In Dispatch" or later, this is the RETURN transit (last step).
  const returnTriggers = new Set(["In Dispatch", "Repairing", "Received"]);
  const isReturn = (statusHistory || []).some(h => returnTriggers.has(h.status));
  return isReturn ? indices[indices.length - 1] : indices[0];
};

// ── POST-RECEIVED Pipeline (7 stages shown when job is Received or later) ────
// These replace the 9-step LEGACY_PIPELINE once the device is at the service center.
export const POST_RECEIVED_PIPELINE = [
  { status:"Received",           icon:PackageOpen,  dot:"#475569", bg:"bg-slate-100",  text:"text-slate-700",   border:"border-slate-200",  emoji:"🏪", desc:"Device arrived at service center"          },
  { status:"Under Inspection",   icon:Search,       dot:"#2563eb", bg:"bg-blue-50",    text:"text-blue-800",    border:"border-blue-200",   emoji:"🔍", desc:"Device is being inspected"                 },
  { status:"Approval Pending",   icon:Clock,        dot:"#d97706", bg:"bg-amber-50",   text:"text-amber-800",   border:"border-amber-200",  emoji:"⏳", desc:"Awaiting customer or admin approval"       },
  { status:"Repair in Progress", icon:Wrench,       dot:"#ea580c", bg:"bg-orange-50",  text:"text-orange-800",  border:"border-orange-200", emoji:"🔧", desc:"Device under repair by technician"         },
  { status:"Repaired",           icon:CheckCircle2, dot:"#059669", bg:"bg-emerald-50", text:"text-emerald-800", border:"border-emerald-200",emoji:"✅", desc:"Repair is complete"                        },
  { status:"Dispatched",         icon:Send,         dot:"#ea580c", bg:"bg-orange-50",  text:"text-orange-800",  border:"border-orange-200", emoji:"📮", desc:"Device packed and dispatched to customer"  },
  { status:"Completed",          icon:Star,         dot:"#16a34a", bg:"bg-green-50",   text:"text-green-800",   border:"border-green-200",  emoji:"🎉", desc:"Service successfully completed"            },
];

// Maps any stored status value → POST_RECEIVED_PIPELINE index
const POST_RX_IDX = {
  "Received":0,
  "Under Inspection":1,
  "Waiting for Approval":2,"Approval Pending":2,
  "Repair in Progress":3,"Repairing":3,
  "Repaired":4,
  "In Dispatch":5,"Dispatched":5,"Replacement Approved":5,"Replacement Dispatched":5,
  "Completed":6,"Warranty Rejected":6,
};
// Maps POST_RECEIVED_PIPELINE status → aliases to search in status_history
export const POST_RX_HISTORY = {
  "Received":           ["Received"],
  "Under Inspection":   ["Under Inspection"],
  "Approval Pending":   ["Waiting for Approval","Approval Pending"],
  "Repair in Progress": ["Repair in Progress","Repairing"],
  "Repaired":           ["Repaired"],
  "Dispatched":         ["In Dispatch","Dispatched","Replacement Dispatched","Replacement Approved"],
  "Completed":          ["Completed","Warranty Rejected"],
};

// Returns true when a job is at "Received" or any later service stage
const POST_RX_TRIGGERS = new Set([
  "Received","Under Inspection","Waiting for Approval","Approval Pending",
  "Repair in Progress","Repairing","Repaired",
  "In Dispatch","Dispatched","Replacement Approved","Replacement Dispatched",
  "Completed","Warranty Rejected",
]);
export const isPostReceived = (status, statusHistory = []) => {
  if (POST_RX_TRIGGERS.has(status)) return true;
  // Final "In Transit" (return to customer) also counts
  if (status === "In Transit") {
    return (statusHistory||[]).some(h => ["Received","Repairing","In Dispatch"].includes(h.status));
  }
  return false;
};

export const getPostReceivedIdx = (status) =>
  status === "In Transit" ? 6 : (POST_RX_IDX[status] ?? -1);

// DD/MM/YYYY HH:MM formatter for post-received timestamps
export const fmtPostReceived = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const p = n => n.toString().padStart(2,"0");
  return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Get step info for any status (new or legacy)
export const getStep = (status) => {
  const primary = PIPELINE.find(s => s.status === status);
  if (primary) return primary;
  const legacy  = LEGACY_PIPELINE.find(s => s.status === status);
  if (legacy)  return legacy;
  return PIPELINE[0]; // default to Received
};

export const getPipelineIndex = (status) => {
  const primary = PIPELINE.findIndex(s => s.status === status);
  if (primary >= 0) return primary;
  const legacy  = LEGACY_PIPELINE.findIndex(s => s.status === status);
  if (legacy  >= 0) return Math.round((legacy / LEGACY_PIPELINE.length) * (PIPELINE.length - 1));
  return 0;
};

export const StatusBadge = ({ status, size = "sm" }) => {
  const step = getStep(status);
  const sz   = size === "lg" ? "text-sm px-3.5 py-1.5" : "text-xs px-2.5 py-0.5";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium border ${sz} ${step.bg} ${step.text} ${step.border}`}
      data-testid="job-status-badge"
    >
      <span>{step.emoji}</span>
      {status}
    </span>
  );
};
